<template>
  <SoDialog
    :model-value="modelValue"
    :card-title="`Sync to ${target}?`"
    :max-width="560"
    data-testid="sync-dialog"
    @update:model-value="close"
  >
    <p class="mb-4">
      Create the same version of
      <strong>{{ payload?.communicationName }}</strong> on {{ target }}. Nova on
      {{ target }} opens in a new tab and asks you to confirm before it
      {{ payload?.makeLive ? "publishes" : "saves" }} the version there.
    </p>
    <SoAlert
      v-if="encodeFailed"
      type="error"
      class="mb-4"
      text="Couldn't prepare the sync link."
      data-testid="sync-encode-error"
    />
    <div class="d-flex justify-end ga-2 mt-2">
      <SoButton
        title="I'll do it later"
        data-testid="sync-dismiss"
        @click="close"
      />
      <SoButton
        :title="`Open ${target}`"
        type="primary"
        icon="external-link sm"
        :href="syncUrl ?? undefined"
        :disabled="!syncUrl"
        :loading="!syncUrl && !encodeFailed"
        target="_blank"
        data-testid="sync-open"
        @click="close"
      />
    </div>
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import { IEmailTemplateSyncPayload } from "@/models/admin/emailTemplates/emailTemplates.types"
  import {
    buildSyncUrl,
    syncTargetEnvironment,
  } from "../helpers/emailTemplateSync"

  export default defineComponent({
    components: { SoDialog, SoButton, SoAlert },
    props: {
      modelValue: {
        type: Boolean,
        default: false,
      },
      payload: {
        type: Object as PropType<IEmailTemplateSyncPayload | null>,
        default: null,
      },
    },
    emits: ["update:modelValue", "closed"],
    data() {
      return {
        target: syncTargetEnvironment(),
        syncUrl: null as string | null,
        encodeFailed: false,
      }
    },
    watch: {
      payload: {
        immediate: true,
        async handler(payload: IEmailTemplateSyncPayload | null) {
          this.syncUrl = null
          this.encodeFailed = false
          if (!payload) return
          try {
            const url = await buildSyncUrl(payload, this.target)
            if (payload === this.payload) this.syncUrl = url
          } catch {
            if (payload === this.payload) this.encodeFailed = true
          }
        },
      },
    },
    methods: {
      close() {
        this.$emit("update:modelValue", false)
        this.$emit("closed")
      },
    },
  })
</script>
